import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MarkdownRenderer } from "@/components/common/markdown-renderer";
import type { EvtxSummaryResponse } from "@/hooks/queries/use-fogbugz";

/** What the summary dialog shows, independent of where the summary came from. */
export interface EvtxSummaryView {
  fileName: string;
  summaryMarkdown: string;
  aiGenerated: boolean;
  /** Line under the title saying where the summary was or will be attached. */
  description: string;
  /** Present only when parsing stopped early at a record or time limit. */
  truncation?: { recordsParsed: number; recordsSeen: number };
}

interface EvtxSummaryViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The summary to show, or null when nothing is shown. */
  summary: EvtxSummaryView | null;
}

/**
 * Show an .evtx summary in a dialog large enough to read it (PI-SPEC-SW-1229).
 *
 * Used for a summary just posted from the ticket page and for one generated
 * during the Formulator interview. The badge tells the reader whether an AI
 * model wrote the summary or the deterministic digest is all there is (no text
 * model configured, or the call failed). Links render as plain text because the
 * summary is built from untrusted event data.
 */
export function EvtxSummaryViewDialog({ open, onOpenChange, summary }: EvtxSummaryViewDialogProps) {
  return (
    <Dialog open={open && summary !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        {summary && (
          <>
            <DialogHeader>
              <div className="flex flex-wrap items-center gap-2 pr-6">
                <DialogTitle className="break-all">Summary of {summary.fileName}</DialogTitle>
                <Badge variant={summary.aiGenerated ? "default" : "secondary"}>
                  {summary.aiGenerated ? "AI-generated" : "Digest only"}
                </Badge>
              </div>
              <DialogDescription>{summary.description}</DialogDescription>
            </DialogHeader>

            {summary.truncation && (
              <p className="text-xs text-amber-500" role="note">
                Parsing stopped early at a record or time limit; the summary covers{" "}
                {summary.truncation.recordsParsed.toLocaleString()} of{" "}
                {summary.truncation.recordsSeen.toLocaleString()} records seen.
              </p>
            )}

            {/*
              The renderer wraps each table in its own overflow-x box, whose scrollbar sits below
              the last row — out of view on a 40-row signature table. Letting the last column
              (Sample message) break anywhere keeps every table within the dialog's width instead.
            */}
            <div className="max-h-[70vh] overflow-y-auto rounded-md border border-white/[0.08] bg-white/[0.02] p-4 text-sm [&_td:last-child]:[overflow-wrap:anywhere]">
              <MarkdownRenderer content={summary.summaryMarkdown} disableLinks />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface EvtxSummaryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The summary just posted to the case, or null when nothing is shown. */
  result: EvtxSummaryResponse["data"] | null;
}

/** Show the summary of an .evtx attachment after it has been posted to the case. */
export function EvtxSummaryDialog({ open, onOpenChange, result }: EvtxSummaryDialogProps) {
  const summary: EvtxSummaryView | null = result
    ? {
        fileName: result.fileName,
        summaryMarkdown: result.summaryMarkdown,
        aiGenerated: result.aiGenerated,
        description: `Posted to the case as ${result.summaryFileName}`,
        ...(result.truncated
          ? {
              truncation: {
                recordsParsed: result.digestStats.recordsParsed,
                recordsSeen: result.digestStats.recordsSeen,
              },
            }
          : {}),
      }
    : null;

  return <EvtxSummaryViewDialog open={open} onOpenChange={onOpenChange} summary={summary} />;
}
